// Helper functions for resizable components
export function calculatePixelValue(
    value: string | number | undefined,
    defaultValue: number
): number {
    if (typeof value === 'number') {
        return value;
    }
    if (typeof value !== 'string') {
        return defaultValue;
    }

    const trimmedValue = value.trim();

    // Handle px
    if (trimmedValue.endsWith('px')) {
        return parseInt(trimmedValue, 10) || defaultValue;
    }

    // Handle rem (assuming 1rem = 16px)
    if (trimmedValue.endsWith('rem')) {
        const num = parseFloat(trimmedValue);
        return !isNaN(num) ? num * 16 : defaultValue;
    }

    // Handle viewport units
    const match = trimmedValue.match(/^([+-]?\d*\.?\d+)(vw|vh|dvw|dvh|vmin|vmax)$/);
    if (match) {
        const num = parseFloat(match[1]);
        const unit = match[2];

        // Need access to window, ensure this helper is only used client-side
        if (typeof window === 'undefined') {
            return defaultValue;
        }
        const { innerWidth, innerHeight } = window;

        switch (unit) {
            case 'vw':
            case 'dvw':
                return (num / 100) * innerWidth;
            case 'vh':
            case 'dvh':
                return (num / 100) * innerHeight;
            case 'vmin':
                return (num / 100) * Math.min(innerWidth, innerHeight);
            case 'vmax':
                return (num / 100) * Math.max(innerWidth, innerHeight);
            default:
                return defaultValue;
        }
    }

    // Handle unitless number string (assume px)
    const num = parseInt(trimmedValue, 10);
    if (!isNaN(num) && num.toString() === trimmedValue) {
        return num;
    }

    // Return default for unhandled units or invalid strings
    console.warn(
        `calculatePixelValue could not parse: "${value}". Using default: ${defaultValue}`
    );
    return defaultValue;
}

export function checkNeedsResizeListener(value: any): boolean {
    if (typeof value === 'string') {
        return /vw|vh|dvw|dvh|vmin|vmax/.test(value.trim());
    }
    // Assume responsive objects might contain viewport units
    if (typeof value === 'object' && value !== null) {
        return true;
    }
    return false;
}
